/**
 * CC Dashboard — Phase 6.
 *
 * Single-CC view aimed at SPOCs. Shows current-month KPIs, a 12-month
 * estimate-vs-actual trend, recent projects, and pending approvals on this CC.
 *
 * Responsive grid:
 *   - 1 column on mobile
 *   - 2 columns on sm
 *   - 4 columns on lg (KPI row)
 * Chart and tables stack on mobile and sit side-by-side on lg.
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { fetchGet } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import MetricTile from "../components/metric_tile";
import DashboardSection from "../components/dashboard_section";
import StatusBadge from "../components/status_badge";
import { CCDashboardData } from "../models/dashboard_models";
import moment from "moment";

const formatCurrency = (n: number) =>
  "₹" + (Number(n) || 0).toLocaleString("en-IN");
const formatShort = (n: number) => {
  const v = Math.abs(n);
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(1) + "Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(1) + "L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n);
};

const CCDashboardScreen: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CCDashboardData | null>(null);

  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/dashboard/cc/${id}`;

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchGet(url);
      if (res.success) setData(res.data);
      else showToast({ type: ToastType.error, text: res.error });
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const utilization = useMemo(() => {
    if (!data) return 0;
    const est = data.kpis.monthly_estimate || 0;
    if (est <= 0) return 0;
    return (data.kpis.mtd_actual / est) * 100;
  }, [data]);

  const series = useMemo(() => {
    if (!data) return [];
    return [
      {
        name: "Estimate",
        data: data.last_12_months.map((m) => Math.round(m.estimate)),
      },
      {
        name: "Actual",
        data: data.last_12_months.map((m) => Math.round(m.actual)),
      },
    ];
  }, [data]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "Nunito, sans-serif",
      },
      colors: ["#94a3b8", "#00BEA5"],
      stroke: { curve: "smooth", width: [2, 3] },
      dataLabels: { enabled: false },
      legend: { position: "top", horizontalAlign: "right" },
      xaxis: {
        categories: (data?.last_12_months || []).map((m) =>
          moment(m.month + "-01").format("MMM YY"),
        ),
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: {
          formatter: (v: number) => formatShort(v),
          style: { fontSize: "11px" },
        },
      },
      tooltip: {
        y: { formatter: (v: number) => formatCurrency(v) },
      },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
    } as any;
  }, [data]);

  // Treemap of approved projects in this month, sized by budget. Useful for
  // spotting "where the money's going" at a glance — the biggest tiles are
  // the biggest spend items.
  const treemapSeries = useMemo(() => {
    if (!data || !data.projects_by_spend) return [];
    return [
      {
        data: data.projects_by_spend.map((p) => ({
          x: p.name,
          y: p.budget,
        })),
      },
    ];
  }, [data]);

  const treemapOptions = useMemo(
    () =>
      ({
        chart: {
          type: "treemap",
          toolbar: { show: false },
          fontFamily: "Nunito, sans-serif",
        },
        // Diverse palette so adjacent tiles are visually distinct. distributed
        // mode applies one color per tile rather than per series.
        colors: [
          "#00BEA5", // Kazam teal
          "#0e7490", // cyan-700
          "#7c3aed", // violet-600
          "#f59e0b", // amber-500
          "#3b82f6", // blue-500
          "#ef4444", // red-500
          "#10b981", // emerald-500
          "#8b5cf6", // violet-500
          "#ec4899", // pink-500
          "#14b8a6", // teal-500
          "#f97316", // orange-500
          "#6366f1", // indigo-500
        ],
        legend: { show: false },
        dataLabels: {
          enabled: true,
          style: { fontSize: "12px", fontFamily: "Nunito, sans-serif" },
          formatter: function (text: string, op: any) {
            // Show project name + short-formatted budget on each tile.
            const v = op?.value ?? 0;
            return [text, formatShort(v)] as any;
          },
          offsetY: -4,
        },
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: true,
            shadeIntensity: 0.3,
          },
        },
        tooltip: {
          y: { formatter: (v: number) => formatCurrency(v) },
        },
      }) as any,
    [],
  );

  if (loading && !data) return <LoadingWidget />;
  if (!data)
    return (
      <div className="p-5 text-sm text-gray-500">
        No dashboard data available for this Cost Center.
      </div>
    );

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <button
            onClick={() => navigation.goBack()}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            &larr; Back
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            {data.cc.name}
          </h1>
          <p className="text-xs text-gray-500">
            {data.cc.business_unit_id ? (
              <span
                className="cursor-pointer hover:text-myPrimaryColor hover:underline"
                onClick={() =>
                  navigation.push(
                    `${urls.bu_dashboard}/${data.cc.business_unit_id}`,
                  )
                }
              >
                {data.cc.business_unit_name || "—"}
              </span>
            ) : (
              <span>{data.cc.business_unit_name || "—"}</span>
            )}{" "}
            <span className="text-gray-300">/</span>{" "}
            {data.cc.department_id ? (
              <span
                className="cursor-pointer hover:text-myPrimaryColor hover:underline"
                onClick={() =>
                  navigation.push(
                    `${urls.dept_dashboard}/${data.cc.department_id}`,
                  )
                }
              >
                {data.cc.department_name || "—"}
              </span>
            ) : (
              <span>{data.cc.department_name || "—"}</span>
            )}
            {data.cc.id && (
              <>
                {" "}
                <span className="text-gray-300">·</span>{" "}
                <span className="font-mono">{data.cc.id}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={data.cc.approvalStatus} />
          <StatusBadge status={data.cc.current_month_status} />
          <span className="text-xs text-gray-500">
            Month <b>{data.month}</b>
          </span>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          title="Monthly Estimate"
          value={formatCurrency(data.kpis.monthly_estimate)}
          subtitle="Current planned spend"
          accent="primary"
        />
        <MetricTile
          title="MTD Actual"
          value={formatCurrency(data.kpis.mtd_actual)}
          subtitle={`Utilization ${utilization.toFixed(0)}%`}
          accent={utilization > 100 ? "rose" : "blue"}
          trend={
            data.kpis.monthly_estimate > 0
              ? {
                  value: utilization - 100,
                  suffix: "% vs est",
                  invertColors: true,
                }
              : undefined
          }
        />
        <MetricTile
          title="Balance"
          value={formatCurrency(data.kpis.balance)}
          subtitle={data.kpis.balance < 0 ? "Over budget" : "Available"}
          accent={data.kpis.balance < 0 ? "rose" : "green"}
        />
        <MetricTile
          title="Projects this month"
          value={data.kpis.projects_this_month}
          subtitle="Finance-approved"
          accent="neutral"
        />
      </div>

      {/* Pending renewal callout */}
      {data.cc.current_month_status &&
        data.cc.current_month_status !== "Active" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-sm text-amber-900">
              <b>{data.cc.current_month_status}</b> for{" "}
              <b>{data.cc.current_month || data.month}</b> — action required.
            </div>
            <button
              onClick={() =>
                navigation.push(
                  `/procurement/masters/cost-center/view-cost-center/${data.cc.id}`,
                )
              }
              className="text-xs bg-myPrimaryColor text-white px-3 py-1 rounded-md self-start sm:self-auto"
            >
              Open renewal
            </button>
          </div>
        )}

      {/* 12-month trend + project spend treemap, side by side on lg+. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="12-month estimate vs actual"
          subtitle="Approved project spend rolled up by month"
        >
          <Chart
            key={data.cc.id + "-" + data.month}
            options={chartOptions}
            series={series}
            type="area"
            height={280}
          />
        </DashboardSection>

        <DashboardSection
          title="Project spend breakdown"
          subtitle={
            data.projects_by_spend.length === 0
              ? "No approved projects this month"
              : `Top ${data.projects_by_spend.length} approved project${
                  data.projects_by_spend.length === 1 ? "" : "s"
                } sized by budget`
          }
        >
          {data.projects_by_spend.length === 0 ? (
            <p className="text-xs text-gray-400 py-10 text-center">
              Approve a project on this Cost Center to see it here.
            </p>
          ) : (
            <Chart
              key={"treemap-" + data.cc.id + "-" + data.month}
              options={treemapOptions}
              series={treemapSeries}
              type="treemap"
              height={280}
            />
          )}
        </DashboardSection>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="Pending project approvals"
          subtitle={
            data.pending_approvals.length === 0
              ? "Nothing waiting"
              : `${data.pending_approvals.length} item(s)`
          }
        >
          {data.pending_approvals.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              All caught up.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">Project</th>
                    <th className="text-right font-medium py-1.5">Budget</th>
                    <th className="text-left font-medium py-1.5">Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending_approvals.map((p: any) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-800">
                          {p.name}
                        </div>
                        <div className="text-gray-400 font-mono">{p.id}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatCurrency(p.budget)}
                      </td>
                      <td className="py-2 pl-2">
                        <StatusBadge status={p.current_approval_level} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>

        <DashboardSection
          title="Recent projects"
          subtitle="Last 5 created"
        >
          {data.recent_projects.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              No projects yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">Project</th>
                    <th className="text-right font-medium py-1.5">Budget</th>
                    <th className="text-left font-medium py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_projects.map((p: any) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-800">
                          {p.name}
                        </div>
                        <div className="text-gray-400 font-mono">{p.id}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatCurrency(p.budget)}
                        {p.original_budget != null &&
                          p.original_budget !== p.budget && (
                            <div className="text-amber-700 text-[10px]">
                              orig {formatCurrency(p.original_budget)}
                            </div>
                          )}
                      </td>
                      <td className="py-2 pl-2">
                        <StatusBadge status={p.approvalStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default CCDashboardScreen;
